import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, MailCheck, ShieldCheck, ArrowLeft } from "lucide-react";
import { api, errorMessage } from "../lib/api";
import { Button } from "../components/core/Button";
import { Field, Input } from "../components/core/Field";
import { AuthLayout } from "../components/shared/AuthLayout";
import { cn } from "../lib/utils";

const rules = [
  { label: "8+ characters", test: (p: string) => p.length >= 8 },
  { label: "Uppercase", test: (p: string) => /[A-Z]/.test(p) },
  { label: "Lowercase", test: (p: string) => /[a-z]/.test(p) },
  { label: "Number", test: (p: string) => /\d/.test(p) },
];

export function ForgotPasswordPage() {
  const [params] = useSearchParams();
  const portal = params.get("portal") === "backoffice" ? "backoffice" : "client";
  const isBO = portal === "backoffice";
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await api.post("/auth/forgot-password", { email, portal }); setSent(true); } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <AuthLayout isBO={isBO} testId="forgot-password-page" eyebrow="Account recovery" headline="Forgot your password? We'll email you a reset link." bullets={["Link is valid for 1 hour", "Single use — request again if it expires", "Your data stays untouched"]}>
      <div className="card-crisp p-8 rise">
        <div className="mb-5 flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white"><KeyRound className="h-5 w-5" /></div><div><h2 className="font-display text-base font-bold text-slate-900">Reset password</h2><p className="text-xs text-slate-500">{isBO ? "Back-office account" : "Client administrator account"}</p></div></div>
        {sent ? (
          <div data-testid="forgot-password-sent" className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"><MailCheck className="mb-2 h-5 w-5" />If an account exists for <b>{email}</b>, a reset link is on its way. Check your inbox (and spam) — the link expires in 1 hour.</div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Field label="Email"><Input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus placeholder="you@company.my" /></Field>
            {error && <p data-testid="forgot-error" className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">{error}</p>}
            <Button type="submit" loading={busy} className="w-full" data-testid="forgot-submit-button">Send reset link</Button>
          </form>
        )}
        <p className="mt-4 text-center text-xs"><Link to={isBO ? "/bo/login" : "/login"} className="inline-flex items-center gap-1 font-semibold text-[color:var(--brand)] hover:underline" data-testid="back-to-login-link"><ArrowLeft className="h-3 w-3" />Back to sign in</Link></p>
      </div>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const portal = params.get("portal") === "backoffice" ? "backoffice" : "client";
  const isBO = portal === "backoffice";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const passed = rules.filter((r) => r.test(pw)).length;
  const ready = passed === rules.length && confirm === pw && !!token;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await api.post("/auth/reset-password", { token, new_password: pw }); setDone(true); setTimeout(() => navigate(isBO ? "/bo/login" : "/login"), 2500); } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <AuthLayout isBO={isBO} testId="reset-password-page" eyebrow="Account recovery" headline="Choose a new password." bullets={["At least 8 characters", "Upper & lower case letters", "At least one number"]}>
      <div className="card-crisp p-8 rise">
        <div className="mb-5 flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-900 text-white"><ShieldCheck className="h-5 w-5" /></div><div><h2 className="font-display text-base font-bold text-slate-900">New password</h2><p className="text-xs text-slate-500">Set a strong password for your account</p></div></div>
        {!token && <p data-testid="reset-missing-token" className="mb-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">This link is missing its token. Please use the link from your email.</p>}
        {done ? (
          <div data-testid="reset-success" className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">Password updated. Redirecting you to sign in…</div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Field label="New password"><Input data-testid="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required autoComplete="new-password" autoFocus /></Field>
            <Field label="Confirm password"><Input data-testid="reset-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" className={cn(confirm && confirm !== pw && "border-rose-400")} /></Field>
            <div className="flex h-1.5 gap-1">{rules.map((_, i) => <span key={i} className={cn("flex-1 rounded-full", i < passed ? (passed === rules.length ? "bg-emerald-500" : "bg-amber-400") : "bg-slate-200")} />)}</div>
            <ul className="grid grid-cols-2 gap-1 text-[11px]">{rules.map((r) => <li key={r.label} className={r.test(pw) ? "text-emerald-600" : "text-slate-400"}>• {r.label}</li>)}</ul>
            {confirm && confirm !== pw && <p className="text-xs text-rose-600" data-testid="reset-mismatch">Passwords do not match.</p>}
            {error && <p data-testid="reset-error" className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">{error}</p>}
            <Button type="submit" loading={busy} disabled={!ready} className="w-full" data-testid="reset-submit-button">Update password</Button>
          </form>
        )}
        <p className="mt-4 text-center text-xs"><Link to={isBO ? "/bo/login" : "/login"} className="font-semibold text-[color:var(--brand)] hover:underline" data-testid="reset-back-to-login-link">Back to sign in</Link></p>
      </div>
    </AuthLayout>
  );
}
